import jwt from 'jsonwebtoken';
import TagManager from 'react-gtm-module';
import {
  store,
} from '../lib/styleguide/Wrapper';
import {
  doAuthenticateUser,
} from '../actions/Login/LoginActions';
import {
  setModal,
} from '../actions/Global/GlobalActions';
import {
  socketConnect,
} from '../actions/Socket/SocketActions';
import {
  getLocal,
  setLocal,
  removeLocal,
} from './StorageUtil';
import {
  saveTokens,
  refreshHostedContent,
  getSession,
  authenticate,
  logout as authLogout,
} from './AuthUtil';
import API from '../api';
import pkg from '../../../../package.json';

// Interval to check if there is a new version (seconds)
const versionTimerInterval = 60;
// Timer to see if cookies need to be refreshed
let versionTimer = null;
// Only connect to the socket once (let socket reconnect attempts re-establish a connection)
let ranSocketConnect = false;

export const SSO_CLIENT_ID_STORAGE_KEY = 'app:sso:clientId';

export const IDENTITY_SOURCE_FEDERATED = 'federated';
export const IDENTITY_SOURCE_NATIVE = 'native';

/**
 * Reload the app
 */
function reloadApp() {
  window.location.reload();
}

/**
 * Prevent the update app modal from showing until the user manually refreshes
 */
function preventUpdate() {
  setLocal('app:ignoreUpdate', true);
}

async function getProfile(userId, token) {
  const profile = await API.User.getById(userId, { token });

  return profile;
}

async function checkVersion() {
  try {
    const ignoreUpdate = getLocal('app:ignoreUpdate', false);
    const versionResponse = await API.Local.getVersion();

    // Do not prompt to update if they chose to wait until next refresh
    if (!ignoreUpdate
      && versionResponse.version !== undefined
      && versionResponse.version !== pkg.version
    ) {
      // Version mismatch, prompt to reload
      store.dispatch(setModal({
        type: 'confirm',
        confirmProps: {
          text: 'Update to New Version',
          style: {
            color: 'success',
          },
        },
        cancelProps: {
          text: 'Not Yet',
        },
        title: 'Update Available',
        message: (
          <div>
            An update is available for the web application. Would you like to update?
            <div
              style={{
                marginTop: 8,
              }}
            >
              Any unsaved changes will be lost.
            </div>
          </div>
        ),
        onConfirm: reloadApp,
        onCancel: preventUpdate,
      }));
    }
  } catch (err) {
    console.error('checkVersion(): Could not get version', err);
  }
}

/**
 * Start the version check interval timer
 */
function startVersionCheck() {
  versionTimer = setInterval(checkVersion, versionTimerInterval * 1000);
  console.log('Started: Version Check');
}

/**
 * Stop checking for version updates
 */
function stopVersionCheck() {
  clearInterval(versionTimer);
  console.log('Stopped: Version Check');
}

/**
 * This function carries out the tasks that are run every time a user is successfully authenticated
 * @param {* This is an object that contains the response from the api from a successful Authentication} authDetails
 */
async function authenticationTasks(authDetails) {
  const tokens = authDetails.data || {};
  const {
    idToken,
  } = tokens;

  if (!idToken) {
    return null;
  }

  saveTokens(tokens);
  await refreshHostedContent({
    idToken,
  });

  const tokenPayload = jwt.decode(idToken);
  const userId = tokenPayload['custom:vibenomics_id'];
  // Fetch user profile
  const profile = await getProfile(userId, idToken);
  // Auth user object
  const authUser = tokenPayload;

  // Overwrite permissions with fetched profile permissions
  authUser.permissions = profile.permissions;

  // Merge profile with user object
  const userObj = {
    ...profile,
    ...authUser,
  };

  // console.log('User has logged in');
  store.dispatch(doAuthenticateUser(true, userObj));

  if (!ranSocketConnect) {
    // Connect to the socket
    const url = process.env.VAPI_SOCKET_HOST_URL ?? process.env.VAPI_BASE_URL.slice(0, -3);

    store.dispatch(socketConnect({
      url,
    }));

    // do not connect to the socket more than once
    ranSocketConnect = true;
  }

  if (!versionTimer) {
    // only set the version check timer once
    startVersionCheck();
  }

  // start associating user to Google Analytics
  TagManager.dataLayer({
    userId: userObj._id,
  });

  return userObj;
}

/**
 * This function will authenticate the user on login
 * @param {* This is an object with the values equaling the Username and Password as keys} authenticationData
 */
export const setSsoClientId = (clientId) => {
  sessionStorage.setItem(SSO_CLIENT_ID_STORAGE_KEY, clientId);
};

export const getSsoClientId = () => sessionStorage.getItem(SSO_CLIENT_ID_STORAGE_KEY);

export const clearSsoClientId = () => {
  sessionStorage.removeItem(SSO_CLIENT_ID_STORAGE_KEY);
};

export const extractClientIdFromRedirectUrl = (redirectUrl) => {
  const clientId = new URL(redirectUrl).searchParams.get('client_id');

  if (!clientId) {
    throw new Error('Invalid sign-in redirect URL');
  }

  return clientId;
};

export const discoverIdentitySource = async (email) => {
  try {
    const result = await API.Authentication.discoverSso(
      { email },
      { skipAuth: true, silent: true },
    );

    if (!result?.source) {
      throw new Error('Invalid authorization or challenge parameters');
    }

    return result;
  } catch (err) {
    const message = typeof err === 'string' ? err : err.message;
    throw new Error(message || 'Invalid authorization or challenge parameters');
  }
};

const handleAuthenticationResponse = async (authenticationDetails) => {
  const firstResult = Array.isArray(authenticationDetails)
    ? authenticationDetails[0]
    : authenticationDetails;

  if (firstResult?.type === 'AUTHENTICATION.AUTHENTICATED') {
    await authenticationTasks(firstResult);
  } else if (firstResult?.type === 'AUTHENTICATION.CHALLENGED') {
    return firstResult;
  }

  return authenticationDetails;
};

export const exchangeSsoCode = async ({ code, clientId }) => {
  try {
    const authenticationDetails = await API.Authentication.exchangeSsoCode(
      { code, clientId },
      { skipAuth: true, silent: true, returnErrorBody: true },
    );

    return handleAuthenticationResponse(authenticationDetails);
  } catch (err) {
    const error = new Error(err.message || 'Request failed');
    error.body = err.body;
    error.status = err.status;
    throw error;
  }
};

export const authenticateUser = async (authenticationData) => {
  // Getting the authentication details for the new authenticate req
  const authData = {
    authType: 'credentials',
    authParams: {
      username: authenticationData.Username,
      password: authenticationData.Password,
    },
  };
  const authenticationDetails = await authenticate(authData);

  if (authenticationDetails[0].type === 'AUTHENTICATION.AUTHENTICATED') {
    await authenticationTasks(authenticationDetails[0]);
  } else if (authenticationDetails[0].type === 'AUTHENTICATION.CHALLENGED') {
    // Return Auth Response details so LoginContainer knows to show First Time Login Screen
    return authenticationDetails[0];
  }

  return authenticationDetails;
};

/**
 * This function will authenticate the user on login
 * @param {* This is an object with the values equaling the Username and Password as keys} authenticationData
 * @param {* This is a updated password from the user for first time login.
 * If it is preset we will update if, if not we will go with the first time
 * login verification} updatedPassword
 */
export const acceptChallenge = async (challengeDetails, updatedPassword) => {
  if (challengeDetails && updatedPassword) {
    // Getting the details for the new password challenge
    const challengeData = {
      challengeType: 'newPasswordRequired',
      challengeParams: {
        session: challengeDetails.data.session,
        username: challengeDetails.data.externalUserId,
        newPassword: updatedPassword,
      },
    };
    const acceptChallengeResponse = await API.Authentication.acceptChallenge(challengeData);

    if (acceptChallengeResponse[0].type === 'AUTHENTICATION.AUTHENTICATED') {
      await authenticationTasks(acceptChallengeResponse[0]);
    } else if (acceptChallengeResponse[0].type === 'AUTHENTICATION.CHALLENGED') {
      // Return Response details so LoginContainer knows to show First Time Login Screen
      return acceptChallengeResponse[0];
    }

    return acceptChallengeResponse;
  }

  return null;
};

/**
 * This is the function that will reset the password for FORGOTTEN passwords (Part 1)
 */
export const forgotPassword = username => {
  const resetData = {
    username,
  };

  const resetPassResponse = API.Authentication.resetPassword(resetData);
  return resetPassResponse;
};

/**
 * This function resets the password for the user (Part 2)
 * @param {* This is the verification code that was emailed to the user and provided in
 * the verification code box} verificationCode
 * @param {* This is the new password provided by the user} newPassword
 */
export const confirmResetPassword = (username, verificationCode, newPassword) => {
  const passwordData = {
    code: verificationCode,
    username,
    newPassword,
  };

  const resetPassResponse = API.Authentication.confirmPassword(passwordData);
  return resetPassResponse.type === 'AUTHENTICATION.RESET_PASSWORD_CONFIRMED'
    ? 'Password Successfully Reset'
    : resetPassResponse;
};

/**
 * Function that changes the current password for an authenticated user inside of the application
 * @param {oldPassword} is the old password that the user had
 * @param {newPassword} is the new password that the user wants to change it to
 */
export const changeUserPassword = (id, oldPassword, newPassword) => {
  const accessToken = getLocal('app:user:accessToken');

  const changePasswordData = {
    _id: id,
    oldPassword,
    newPassword,
    accessToken,
  };

  const changePassResponse = API.User.changePassword(changePasswordData);
  return changePassResponse;
};

export const getUserInfo = async () => {
  const tokens = await getSession();
  const {
    accessToken,
    idToken,
    refreshToken,
  } = tokens;

  const user = await authenticationTasks({
    data: {
      accessToken,
      idToken,
      refreshToken,
    },
  });

  return user;
};

export const logout = () => {
  // Clear the stored auth data
  authLogout();
  removeLocal('app:ignoreUpdate');
  stopVersionCheck();

  // Tear down the realtime socket so a subsequent login establishes a fresh
  // connection with the new JWT instead of reusing one authenticated with the
  // now-invalid token. The socket's own 'disconnect' handler dispatches
  // socketDisconnected.
  const { connection: socket } = store.getState().socket;

  if (socket && typeof socket.disconnect === 'function') {
    socket.disconnect();
  }

  ranSocketConnect = false;

  // stop associating user to Google Analytics
  TagManager.dataLayer({
    userId: null,
  });
};

/**
 * Sign the user out everywhere: revoke the server-side session (Cognito global
 * sign-out, plus IdP refresh-token revoke for SSO/federated users) and then
 * clear local auth state.
 *
 * The refresh token is always sent when available — it is required to fully
 * sign out an SSO/federated user and ignored for native users. Local state is
 * cleared regardless of the server outcome so the user is never trapped in a
 * logged-in UI if the request fails.
 * @param {string} [refreshToken] current refresh token (defaults to stored token)
 */
export const signOutEverywhere = async (refreshToken = getLocal('app:user:refreshToken')) => {
  try {
    await API.Authentication.logout({ refreshToken }, { silent: true });
  } catch (err) {
    console.error('signOutEverywhere(): server logout failed', err);
  } finally {
    logout();
  }
};

export default {
  getUserInfo,
  authenticateUser,
  acceptChallenge,
  forgotPassword,
  changeUserPassword,
  confirmResetPassword,
  logout,
  signOutEverywhere,
  discoverIdentitySource,
  extractClientIdFromRedirectUrl,
  exchangeSsoCode,
  setSsoClientId,
  getSsoClientId,
  clearSsoClientId,
  SSO_CLIENT_ID_STORAGE_KEY,
  IDENTITY_SOURCE_FEDERATED,
  IDENTITY_SOURCE_NATIVE,
};
